import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';

import { Block } from '@app/core/models/block/block.model';
import { MixedObject } from '@core/interfaces/mixed-object';
import { FilterType } from '@core/models';
import { AdvancedFilterData } from '@shared/services/filters/types';
import { merge, Subscription } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';

@Component({
  selector: 'advanced-filter-select-item',
  styleUrls: ['./advanced-filter-select-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="filter-select-item"
         [class.disabled]="(isDisabled && !isActive)"
         [formGroup]="form">

      <label [ngStyle]="{ '--color': activeColor }" [class]="template.uId" [ngClass]="activeClass" [for]="uuid"
             (keyup.enter)="onCheckedChanged()"
             [tabindex]="template.tabindex"
      >
        <span class="check-indicator" [class.active]="isActive" [ngStyle]="{ '--size': indicatorSize }"></span>
        <input type="checkbox" class="hidden-input" (change)="onCheckedChanged()"
               [id]="uuid" [checked]="isActive"/>
        <div
          app-block
          [item]="template"
        ></div>
      </label>

      @if (item.children && item.children.length > 0) {
        <div class="children-container">
          @for (child of item.children; track child.id; let idx = $index) {
            @if (child.display) {
              <advanced-filter-select-item
                [templates]="templates"
                [level]="level + 1"
                [index]="index + '.' + idx.toString()"
                [item]="child"
                [controlType]="controlType"
                [form]="itemsFormMap.get(child.id)"
                [itemsFormMap]="itemsFormMap"
                [ngStyle]="{display: child.display ? 'block' : 'none'}"
                (checked)="checked.emit($event)"
              >
              </advanced-filter-select-item>
            }
          }
        </div>
      }
    </div>
  `,
  standalone: false,
})
export class AdvancedFilterSelectItemComponent implements OnChanges, OnInit, OnDestroy {
  @Input() level = 0;
  @Input() index = '0';
  @Input() form: FormGroup;
  @Input() item: AdvancedFilterData;
  @Input() controlType: FilterType;
  @Input() templates: Block[];
  @Input() itemsFormMap: Map<number, FormGroup>;
  template: Block;
  @Output() checked = new EventEmitter<any>();
  activeColor: string | null = null;
  indicatorSize: string | null = null;
  private readonly subscription = new Subscription();

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly cdr: ChangeDetectorRef,
  ) {
  }

  get uuid(): string {
    return (this.template?.uId|| '')+'.'+this.index + '';
  }

  get activeClass(): MixedObject {
    const clsActive= (this.template?.uId || '') + '__active';
    if(this.isActive){
      return {
        [clsActive]: true,
      };
    }
    return {};
  }

  get isActive(): boolean {
    return this.form.get('active').value;
  }

  get isDisabled(): boolean {
    return this.form.get('disabled')?.value;
  }

  public ngOnInit(): void {
    const originalTemplate = this.templates.find(t => t.settings.viewPart === 'templateLevel' + this.level);
    // Stwórz własną kopię template dla tego komponentu
    this.template = originalTemplate?.clone();
    this.activeColor = this.template?.styles.all.basic.color || 'currentColor';
    this.indicatorSize = this.template?.styles.all.basic.fontSize || '10px';
    this.updateTemplateContent();
    this.listenForFormChanges();
    // this.form.patchValue({active: this.item['active']});
  }

  ngOnChanges(changes: SimpleChanges): void {
    const item = changes['item'];
    if (this.template && this.item && item && item.currentValue.count !== item.previousValue?.count) {
      const newContent = this.item.name + ' (' + this.item.count + ')';
      // Tylko jeśli content się zmienił, stwórz nową referencję
      if (this.template.content !== newContent) {
        const clone = this.template.clone();
        clone.content = newContent;
        this.template = clone;
      }
    }
  }

  onCheckedChanged() {
    const _active = !this.isActive;
    // Natychmiastowe przełączenie wizualnego stanu wskaźnika bez oczekiwania
    // na pełny cykl change detection Angulara — reszta drzewa (liczniki,
    // faceting) aktualizuje się dalej normalnie w tle. Bez tego użytkownik
    // widzi zaznaczenie dopiero po kilkuset milisekundach.
    const indicator = this.elementRef.nativeElement.querySelector('.check-indicator');
    indicator?.classList.toggle('active', _active);
    this.form.patchValue({active: _active});
    this.checked.emit(this.form.value);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private listenForFormChanges(): void {
    const nameControl = this.form.get('name');
    const countControl = this.form.get('count');
    const activeControl = this.form.get('active');
    const disabledControl = this.form.get('disabled');
    const controls = [nameControl, countControl, activeControl, disabledControl].filter(Boolean);

    if (controls.length === 0) {
      return;
    }

    this.subscription.add(
      merge(...controls.map(control => control.valueChanges.pipe(distinctUntilChanged())))
        .pipe(
          tap(() => {
            this.updateTemplateContent();
            this.cdr.markForCheck();
          }),
        )
        .subscribe(),
    );
  }

  private updateTemplateContent(): void {
    if (!this.template) {
      return;
    }

    const name = this.form.get('name')?.value ?? this.item.name;
    const count = this.form.get('count')?.value ?? this.item.count;
    const newContent = `${name} (${count})`;

    if (this.template.content !== newContent) {
      const clone = this.template.clone();
      clone.content = newContent;
      this.template = clone;
    }
  }
}
